import React, { createContext, ReactNode, useContext } from "react";
import type { MeUserDataType } from "src/app";
import type { Scope, UserRole } from "src/rbac/scopes";

interface MeProviderProps {
  me: MeUserDataType;
  children: ReactNode;
}

const MeContext = createContext<MeUserDataType | null>(null);

export const MeProvider = ({ me, children }: MeProviderProps) => {
  return <MeContext.Provider value={me}>{children}</MeContext.Provider>;
};

export const useMe = () => {
  const context = useContext(MeContext);
  if (context === null) {
    throw new Error("useMe must be used within a MeProvider");
  }
  return context;
};

export const usePermissions = () => {
  const me = useMe();
  const scopes = new Set(me.scopes || []);

  return {
    role: me.role as UserRole,
    scopes: me.scopes || [],
    isSuperAdmin: me.isSuperAdmin || false,

    // Check if user has a specific scope
    hasScope: (scope: Scope) => scopes.has(scope),

    // Convenience methods for common checks
    canRead: (domain: string) => scopes.has(`read:${domain}` as Scope),
    canWrite: (domain: string) => scopes.has(`write:${domain}` as Scope),
  };
}; 